// ProjectPendingChangesModal.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
  CSpinner,
  CBadge,
  CAlert,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilCalendar,
  cilSpreadsheet,
  cilChartLine,
  cilPeople,
  cilWarning,
  cilArrowTop,
  cilArrowBottom,
  cilCheckCircle,
  cilTrash,
  cilXCircle,
  cilList,
} from "@coreui/icons";
import { apiClient } from "services/Api";

/**
 * Modal de revisão das alterações propostas pelo cliente.
 *
 * Props:
 * - clientId: number
 * - projectId: number
 * - onClose: () => void
 * - setToast: ({ title, type, message }) => void
 * - onApplied?: () => void  // callback após aplicar alterações (para refrescar página pai)
 * - onRequestReview?: (personId: number, target?: "salaries") => void
 */
export default function ProjectPendingChangesModal({
  clientId,
  projectId,
  onClose,
  setToast,
  onApplied,
  onRequestReview = () => {},
  projectState,
}) {
  const isTheoretical = projectState === "THEORETICAL";
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [expandedPersons, setExpandedPersons] = useState({});
  const [applyingAll, setApplyingAll] = useState(false);
  const [applyingPersonId, setApplyingPersonId] = useState(null);
  const [applyingWorkingDays, setApplyingWorkingDays] = useState(false);
  const [discardingWorkingDays, setDiscardingWorkingDays] = useState(false);
  const [discardingAll, setDiscardingAll] = useState(false);
  const [discardingPersonId, setDiscardingPersonId] = useState(null);
  const [monthActionLoading, setMonthActionLoading] = useState({});

  const loadSummary = useCallback(
    (options = {}) => {
      const { silent = false } = options;
      if (!silent) setLoading(true);
      apiClient
        .get(`/projects/management/${clientId}/${projectId}/pending_changes/`)
        .then((response) => {
          setSummary(response.data);
        })
        .catch((error) => {
          console.error(error);
          setToast({
            title: "Alterações do Cliente",
            type: "error",
            message: "Não foi possível carregar as alterações pendentes.",
          });
          setSummary(null);
        })
        .finally(() => {
          if (!silent) setLoading(false);
        });
    },
    [clientId, projectId, setToast]
  );

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const optimisticRemovePerson = useCallback(
    (personId) => {
      setSummary((prev) => {
        if (!prev?.persons) return prev;
        const filtered = prev.persons.filter(
          (person) => person.person_id !== personId
        );
        if (filtered.length === prev.persons.length) return prev;
        return {
          ...prev,
          persons: filtered,
        };
      });
      setExpandedPersons((prev) => {
        if (!prev?.[personId]) return prev;
        const next = { ...prev };
        delete next[personId];
        return next;
      });
    },
    [setSummary, setExpandedPersons]
  );

  const optimisticRemovePersonMonth = useCallback(
    (personId, monthDate) => {
      const monthKey = String(monthDate);
      let removedPerson = false;
      setSummary((prev) => {
        if (!prev?.persons) return prev;
        let changed = false;
        const persons = [];
        for (const person of prev.persons) {
          if (person.person_id !== personId) {
            persons.push(person);
            continue;
          }
          const monthlyRows = person.monthly_rows || [];
          const filteredRows = monthlyRows.filter(
            (row) => String(row.date) !== monthKey
          );
          if (filteredRows.length === monthlyRows.length) {
            persons.push(person);
            continue;
          }
          changed = true;
          if (filteredRows.length === 0) {
            removedPerson = true;
            continue;
          }
          persons.push({ ...person, monthly_rows: filteredRows });
        }
        if (!changed) return prev;
        return {
          ...prev,
          persons,
        };
      });
      if (removedPerson) {
        setExpandedPersons((prev) => {
          if (!prev?.[personId]) return prev;
          const next = { ...prev };
          delete next[personId];
          return next;
        });
      }
    },
    [setSummary, setExpandedPersons]
  );

  const pendingChangesAction = useCallback(
    (action, payload) =>
      apiClient.post(
        `/projects/management/${clientId}/${projectId}/pending_changes/${action}/`,
        payload
      ),
    [clientId, projectId]
  );

  const hasAnyChanges =
    summary &&
    ((summary.workingdays && summary.workingdays.has_changes) ||
      (summary.persons && summary.persons.length > 0));
  const projectIsSalaryBased = summary?.is_salary_based;
  const salaryAccessAllowed =
    summary?.salary_access_allowed !== undefined
      ? summary.salary_access_allowed
      : true;
  const canShowSalaryFields = projectIsSalaryBased && salaryAccessAllowed;

  const formatMonthLabel = (value) => {
    if (!value) return "—";
    const str = String(value);
    const match = str.match(/(\d{4})-(\d{2})/);
    if (match) {
      return `${match[1]}-${match[2]}`;
    }
    return str;
  };

  const formatDeltaHours = (value) => {
    if (value == null || Number.isNaN(Number(value))) return "0.0 h";
    const num = Number(value);
    return `${num > 0 ? "+" : ""}${num.toFixed(1)} h`;
  };

  const renderDeltaIcon = (value) => {
    if (value == null || Number(value) === 0) {
      return <CIcon icon={cilArrowTop} className="text-muted" />;
    }
    const positive = Number(value) > 0;
    return (
      <CIcon
        icon={positive ? cilArrowTop : cilArrowBottom}
        className={positive ? "text-success" : "text-danger"}
      />
    );
  };

  const handleApplyWorkingDays = () => {
    const rows = summary?.workingdays?.months || [];
    if (!rows.length) return;
    const months = rows.map((row) => row.date);
    setApplyingWorkingDays(true);
    pendingChangesAction("apply", { scope: "working_days", months })
      .then(() => {
        setToast({
          title: "Dias Úteis",
          type: "success",
          message: "Alterações aos dias úteis aplicadas.",
        });
        loadSummary();
        if (onApplied) onApplied();
      })
      .catch((error) => {
        console.error(error);
        setToast({
          title: "Dias Úteis",
          type: "error",
          message: "Não foi possível aplicar as alterações aos dias úteis.",
        });
      })
      .finally(() => setApplyingWorkingDays(false));
  };

  const handleDiscardWorkingDays = () => {
    const rows = summary?.workingdays?.months || [];
    if (!rows.length) return;
    const months = rows.map((row) => row.date);
    setDiscardingWorkingDays(true);
    pendingChangesAction("discard", { scope: "working_days", months })
      .then(() => {
        setToast({
          title: "Dias Úteis",
          type: "success",
          message: "Alterações aos dias úteis descartadas.",
        });
        loadSummary();
        if (onApplied) onApplied();
      })
      .catch((error) => {
        console.error(error);
        setToast({
          title: "Dias Úteis",
          type: "error",
          message: "Não foi possível descartar as alterações aos dias úteis.",
        });
      })
      .finally(() => setDiscardingWorkingDays(false));
  };

  const handleDiscardAllChanges = () => {
    setDiscardingAll(true);
    pendingChangesAction("discard", { scope: "all" })
      .then(() => {
        setToast({
          title: "Alterações do Cliente",
          type: "success",
          message: "Todas as alterações foram descartadas.",
        });
        loadSummary();
        if (onApplied) onApplied();
      })
      .catch((error) => {
        console.error(error);
        setToast({
          title: "Alterações do Cliente",
          type: "error",
          message: "Não foi possível descartar todas as alterações.",
        });
      })
      .finally(() => setDiscardingAll(false));
  };

  const handleDiscardPersonChanges = (personId) => {
    setDiscardingPersonId(personId);
    pendingChangesAction("discard", { scope: "person", person_id: personId })
      .then(() => {
        setToast({
          title: "Alterações do Cliente",
          type: "success",
          message: "Alterações deste colaborador descartadas.",
        });
        optimisticRemovePerson(personId);
        loadSummary({ silent: true });
        if (onApplied) onApplied();
      })
      .catch((error) => {
        console.error(error);
        setToast({
          title: "Alterações do Cliente",
          type: "error",
          message:
            "Não foi possível descartar as alterações deste colaborador.",
        });
      })
      .finally(() => setDiscardingPersonId(null));
  };

  const renderOverview = () => {
    if (!summary?.overview) return null;
    const overview = summary.overview;
    const workingdayChanges = overview.workingdays?.changes ?? 0;
    const sheetInfo = overview.sheets || {};
    const projectHours = overview.project_hours || {};
    const otherHours = overview.other_hours || {};
    const availability = overview.availability || {};
    const sheetMonths = sheetInfo.months ?? 0;
    const sheetPersons = sheetInfo.persons ?? 0;
    const salaryChanges = sheetInfo.salary_changes ?? 0;
    const ssChanges = sheetInfo.ss_changes ?? 0;
    const projectMonths = projectHours.months ?? 0;
    const projectPersons = projectHours.persons ?? 0;
    const projectDelta = projectHours.total_delta ?? 0;
    const otherMonths = otherHours.months ?? 0;
    const otherPersons = otherHours.persons ?? 0;
    const otherDelta = otherHours.total_delta ?? 0;
    const availabilityMonths = availability.months ?? 0;
    const availabilityPersons = availability.persons ?? 0;
    const availabilityOverPersons = availability.persons_over_limit ?? 0;
    const availabilityOverMonths = availability.over_limit_months ?? 0;

    const cards = [
      {
        key: "sheets",
        title: "Folhas & salários",
        value: sheetMonths,
        description: `${sheetPersons} RH com alterações${
          canShowSalaryFields
            ? ` • ${salaryChanges} salário(s) / ${ssChanges} SS`
            : projectIsSalaryBased
            ? " • Salários restritos"
            : ""
        }`,
        highlight: sheetMonths > 0,
        icon: cilSpreadsheet,
      },
      {
        key: "workingdays",
        title: "Dias úteis",
        value: workingdayChanges,
        description:
          workingdayChanges === 1
            ? "1 mês com alteração"
            : `${workingdayChanges} meses com alteração`,
        highlight: workingdayChanges > 0,
        icon: cilCalendar,
      },
      {
        key: "project",
        title: "Horas do projeto",
        value: projectMonths,
        description: `${projectPersons} RH • Δ ${formatDeltaHours(
          projectDelta
        )}`,
        highlight: Math.abs(projectDelta) > 0.01,
        icon: cilChartLine,
      },
      {
        key: "other",
        title: "Outros projetos",
        value: otherMonths,
        description: `${otherPersons} RH • Δ ${formatDeltaHours(otherDelta)}`,
        highlight: Math.abs(otherDelta) > 0.01,
        icon: cilPeople,
      },
      {
        key: "capacity",
        title: "Afetação",
        value: availabilityMonths,
        description: `${availabilityPersons} RH analisados${
          availabilityOverPersons
            ? ` • ${availabilityOverPersons} com excedente (${availabilityOverMonths} ${
                availabilityOverMonths === 1 ? "mês" : "meses"
              })`
            : ""
        }`,
        highlight: availabilityOverPersons > 0,
        icon: cilWarning,
        tag: availabilityOverPersons ? "Excede capacidade" : null,
      },
    ];

    return (
      <div className="mb-4">
        <div className="row g-3">
          {cards.map((card) => (
            <div key={card.key} className="col-12 col-md-6 col-lg-4 col-xxl-3">
              <div
                className={`border rounded p-3 h-100 shadow-sm ${
                  card.highlight ? "bg-light border-success" : ""
                }`}
              >
                <div className="d-flex align-items-center gap-2 mb-2">
                  <CIcon icon={card.icon} className="text-muted" />
                  <span className="text-uppercase small text-muted">
                    {card.title}
                  </span>
                </div>
                <div className="fs-4 fw-semibold">{card.value}</div>
                <div className="small text-muted">{card.description}</div>
                {card.tag && (
                  <CBadge color="danger" className="mt-2">
                    {card.tag}
                  </CBadge>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleTogglePerson = (personId) => {
    setExpandedPersons((prev) => ({
      ...prev,
      [personId]: !prev[personId],
    }));
  };

  const applyAllChanges = () => {
    setApplyingAll(true);
    pendingChangesAction("apply", { scope: "all" })
      .then(() => {
        setToast({
          title: "Alterações do Cliente",
          type: "success",
          message: "Todas as alterações foram aplicadas com sucesso.",
        });
        if (onApplied) onApplied();
        onClose();
      })
      .catch((error) => {
        console.error(error);
        setToast({
          title: "Alterações do Cliente",
          type: "error",
          message: "Ocorreu um erro ao aplicar as alterações.",
        });
      })
      .finally(() => setApplyingAll(false));
  };

  const applyPersonChanges = (personId) => {
    setApplyingPersonId(personId);
    pendingChangesAction("apply", { scope: "person", person_id: personId })
      .then(() => {
        setToast({
          title: "Alterações do Cliente",
          type: "success",
          message: "Alterações deste colaborador aplicadas com sucesso.",
        });
        optimisticRemovePerson(personId);
        loadSummary({ silent: true });
        if (onApplied) onApplied();
      })
      .catch((error) => {
        console.error(error);
        setToast({
          title: "Alterações do Cliente",
          type: "error",
          message:
            "Ocorreu um erro ao aplicar as alterações deste colaborador.",
        });
      })
      .finally(() => setApplyingPersonId(null));
  };

  const handlePersonMonthAction = (personId, monthDate, actionType) => {
    const key = `${personId}-${monthDate}`;
    setMonthActionLoading((prev) => ({ ...prev, [key]: actionType }));
    pendingChangesAction(actionType, {
      scope: "person_month",
      person_id: personId,
      months: [monthDate],
    })
      .then(() => {
        setToast({
          title: "Alterações do Cliente",
          type: "success",
          message:
            actionType === "apply"
              ? "Alterações deste mês aplicadas."
              : "Alterações deste mês descartadas.",
        });
        optimisticRemovePersonMonth(personId, monthDate);
        loadSummary({ silent: true });
        if (onApplied) onApplied();
      })
      .catch((error) => {
        console.error(error);
        setToast({
          title: "Alterações do Cliente",
          type: "error",
          message:
            actionType === "apply"
              ? "Não foi possível aplicar estas alterações."
              : "Não foi possível descartar estas alterações.",
        });
      })
      .finally(() =>
        setMonthActionLoading((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        })
      );
  };

  const renderWorkingDaysSection = () => {
    if (!summary?.workingdays?.has_changes) return null;

    const rows = summary.workingdays.months || [];

    return (
      <div className="mb-4">
        <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center mb-2">
          <h5 className="d-flex align-items-center gap-2 mb-0">
            Dias Úteis
            <CBadge color="warning">
              {rows.length} alteração{rows.length === 1 ? "" : "s"}
            </CBadge>
          </h5>
          <div className="d-flex flex-wrap gap-2">
            <CButton
              color="primary"
              variant="outline"
              size="sm"
              disabled={applyingWorkingDays}
              onClick={handleApplyWorkingDays}
            >
              {applyingWorkingDays ? (
                <CSpinner size="sm" className="me-2" />
              ) : (
                <CIcon icon={cilCheckCircle} className="me-2" />
              )}
              Aplicar dias úteis
            </CButton>
            <CButton
              color="danger"
              variant="outline"
              size="sm"
              disabled={discardingWorkingDays}
              onClick={handleDiscardWorkingDays}
            >
              {discardingWorkingDays ? (
                <CSpinner size="sm" className="me-2" />
              ) : (
                <CIcon icon={cilTrash} className="me-2" />
              )}
              Descartar dias úteis
            </CButton>
          </div>
        </div>
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Mês</th>
                <th>Dias atuais</th>
                <th>Dias propostos</th>
                <th>Tipo</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={String(row.date)}>
                  <td>{formatMonthLabel(row.date)}</td>
                  <td>{row.current ? row.current.day : "—"}</td>
                  <td>{row.pending?.day ?? "—"}</td>
                  <td>
                    {row.change_type === "added" && (
                      <CBadge color="success">Novo</CBadge>
                    )}
                    {row.change_type === "modified" && (
                      <CBadge color="warning">Alterado</CBadge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <small className="text-muted">
          Pode aplicar ou descartar os dias úteis propostos sem fechar o
          restante modal.
        </small>
      </div>
    );
  };

  const renderPersonRow = (person) => {
    const isExpanded = !!expandedPersons[person.person_id];

    const sheetByMonth = person.sheet_by_month || [];
    const monthlyRows = person.monthly_rows || [];
    const summaryInfo = person.summary || {};
    const contract = person.contract || {};

    const hasSheetChanges = !!summaryInfo.has_sheet_changes;
    const hasActivityChanges =
      summaryInfo.has_project_activity_changes ||
      summaryInfo.has_other_activity_changes;
    const hasCapacityDetails = monthlyRows.some((row) => row.allocation);
    const hasWorkingDayChanges = monthlyRows.some((row) => {
      const wd = row.working_days;
      if (!wd) return false;
      return wd.current !== wd.pending;
    });

    const fmtPair = (curr, pend) => {
      if (curr == null && pend == null) return "—";
      if (curr == null) return `— → ${pend}`;
      if (pend == null) return `${curr} → —`;
      if (curr === pend) return String(curr);
      return `${curr} → ${pend}`;
    };

    const fmtHoursPair = (before, after, precision = 1) => {
      const format = (value) =>
        value == null ? "—" : Number(value).toFixed(precision);
      if (before == null && after == null) return "—";
      if (after == null) {
        return format(before);
      }
      if (before == null) {
        return `— → ${format(after)}`;
      }
      if (after === before) {
        return format(before);
      }
      return `${format(before)} → ${format(after)}`;
    };

    const totalDeltaHours = summaryInfo.total_hours_delta ?? 0;
    const hasOverPending = !!summaryInfo.has_overallocation;

    const renderSheetCell = (sheetData, workingDays) => {
      const c = sheetData?.current || null;
      const p = sheetData?.pending || null;
      const changed = new Set((sheetData && sheetData.changed_fields) || []);
      const highlight = (field) =>
        changed.has(field) ? "fw-semibold text-warning" : "";
      const hasPending = !!p;

      const formatSheetValue = (field) => {
        if (!c) return "—";
        const currVal = c[field];
        const pendingVal = p ? p[field] : null;
        if (!hasPending) {
          return currVal ?? "—";
        }
        if (field === "ss") {
          return fmtHoursPair(currVal * 100, pendingVal * 100, 2);
        }
        return fmtPair(currVal, pendingVal);
      };

      const formatWorkingDaysValue = () => {
        if (!workingDays) return "—";
        return fmtPair(workingDays.current, workingDays.pending);
      };

      if (!c) {
        return (
          <div className="small">
            <div className="text-muted">Sem dados de folha para este mês.</div>
            <div>Dias úteis do projeto: {formatWorkingDaysValue()}</div>
          </div>
        );
      }

      return (
        <div className="small">
          <div className={highlight("daily_journey")}>
            Jornada: {formatSheetValue("daily_journey")}
          </div>
          <div className={highlight("fouls")}>
            Faltas: {formatSheetValue("fouls")}
          </div>
          {canShowSalaryFields && (
            <>
              <div className={highlight("salary")}>
                Salário: {formatSheetValue("salary")}
              </div>
              <div className={highlight("ss")}>
                SS: {formatSheetValue("ss")}
              </div>
            </>
          )}
          <div>Dias úteis do projeto: {formatWorkingDaysValue()}</div>
        </div>
      );
    };

    const renderActivitiesCell = (rows, { highlightManaged = false } = {}) => {
      if (!rows.length) {
        return <div className="text-muted small">(sem alterações diretas)</div>;
      }

      return (
        <div className="d-flex flex-column gap-1">
          {rows.map((r) => (
            <div
              key={`${person.person_id}-${r.activity_id}-${
                r.project_id
              }-${String(r.date)}`}
              className="small"
            >
              <strong>{r.activity_name}</strong>{" "}
              <span className="text-muted">({r.project_name})</span>
              {highlightManaged && (
                <CBadge
                  color={r.project_managed ? "primary" : "secondary"}
                  className="ms-1"
                >
                  {r.project_managed ? "Gerido" : "Não gerido"}
                </CBadge>
              )}
              <div className="d-flex align-items-center gap-1">
                {renderDeltaIcon(r.delta_real)}
                <span>
                  {r.real_current.toFixed(1)} → {r.real_pending.toFixed(1)} (
                  {r.delta_real > 0 ? "+" : ""}
                  {r.delta_real.toFixed(1)} h)
                </span>
              </div>
            </div>
          ))}
        </div>
      );
    };

    const renderCapacityCell = (
      alloc,
      projectRows,
      otherRows,
      sheetEntry,
      workingDays,
      projectHours
    ) => {
      if (
        !alloc &&
        !projectRows.length &&
        !otherRows.length &&
        (!sheetEntry || !sheetEntry.current)
      ) {
        return <div className="text-muted small">Sem dados.</div>;
      }

      const totalRows = [...projectRows, ...otherRows];
      const rowsDelta = totalRows.reduce(
        (acc, r) => acc + (r.delta_real || 0),
        0
      );

      const toNumberOrNull = (value) =>
        value == null || value === "" ? null : Number(value);
      const toNumberOrZero = (value) =>
        value == null || value === "" ? 0 : Number(value);

      const assignedCurrent =
        alloc?.assigned_current ??
        totalRows.reduce((acc, r) => acc + (r.real_current || 0), 0);
      const assignedAfter =
        alloc?.assigned_pending ??
        (alloc
          ? alloc?.assigned_current ?? assignedCurrent
          : assignedCurrent + rowsDelta);

      const sheetCurrent = sheetEntry?.current || {};
      const sheetPending = sheetEntry?.pending || null;
      const djCurrent = toNumberOrNull(sheetCurrent.daily_journey);
      const djAfter = toNumberOrNull(
        sheetPending && sheetPending.daily_journey != null
          ? sheetPending.daily_journey
          : djCurrent
      );
      const foulsCurrent = toNumberOrZero(sheetCurrent.fouls);
      const foulsAfter = toNumberOrZero(
        sheetPending && sheetPending.fouls != null
          ? sheetPending.fouls
          : foulsCurrent
      );

      const wdCurrent = toNumberOrNull(
        workingDays?.current ??
          (workingDays?.pending != null ? workingDays.pending : null)
      );
      const wdAfter = toNumberOrNull(
        workingDays?.pending != null ? workingDays.pending : wdCurrent
      );

      const calcBase = (dj, wd, fouls) => {
        if (dj == null || wd == null) return null;
        const foulsHours = fouls ?? 0;
        return dj * wd - foulsHours;
      };

      const baseCurrent = calcBase(djCurrent, wdCurrent, foulsCurrent);
      const baseAfter = calcBase(djAfter, wdAfter, foulsAfter);

      const capacityCurrent =
        baseCurrent != null ? baseCurrent - assignedCurrent : null;
      const capacityAfter =
        baseAfter != null ? baseAfter - assignedAfter : null;

      const projectAssignedCurrent =
        projectHours && projectHours.current != null
          ? Number(projectHours.current)
          : projectRows.reduce((acc, r) => acc + (r.real_current || 0), 0);
      const projectAssignedAfter =
        projectHours && projectHours.pending != null
          ? Number(projectHours.pending)
          : projectRows.reduce((acc, r) => acc + (r.real_pending || 0), 0);

      const remainingText = fmtHoursPair(capacityCurrent, capacityAfter);
      const remainingNegative =
        (capacityCurrent != null && capacityCurrent < 0) ||
        (capacityAfter != null && capacityAfter < 0);

      return (
        <div className="small">
          <div>
            Horas restantes:{" "}
            <span
              className={remainingNegative ? "text-danger fw-semibold" : ""}
            >
              {remainingText}
            </span>
          </div>
          {remainingNegative && (
            <CBadge color="danger" size="sm" className="mt-1">
              Excede capacidade
            </CBadge>
          )}

          <div className="mt-2">
            Horas atribuídas ao projeto:{" "}
            {fmtHoursPair(projectAssignedCurrent, projectAssignedAfter)}
          </div>
        </div>
      );
    };

    const pluralize = (value) => (value === 1 ? "mês" : "meses");

    const badges = [];
    if (summaryInfo.has_sheet_changes) {
      const count = summaryInfo.sheet_months || sheetByMonth.length;
      badges.push(`Folha (${count} ${pluralize(count)})`);
    }
    if (summaryInfo.has_salary_change && canShowSalaryFields)
      badges.push("Salário");
    if (summaryInfo.has_ss_change) badges.push("SS");
    if (summaryInfo.has_project_activity_changes) {
      const count = summaryInfo.project_activity_months || 0;
      badges.push(`Projeto (${count} ${pluralize(count)})`);
    }
    if (summaryInfo.has_other_activity_changes) {
      const count = summaryInfo.other_activity_months || 0;
      badges.push(`Outros projetos (${count} ${pluralize(count)})`);
    }

    return (
      <div key={person.person_id} className="border rounded p-3 mb-3 bg-light">
        <div className="d-flex justify-content-between flex-wrap gap-3">
          <div>
            <div className="d-flex align-items-center gap-2">
              <strong>{person.person_name}</strong>
              {hasOverPending && (
                <CBadge color="danger" size="sm">
                  Excede capacidade
                </CBadge>
              )}
            </div>
            {(contract.start || contract.end) && (
              <div className="small text-muted">
                Contrato: {formatMonthLabel(contract.start)} →{" "}
                {formatMonthLabel(contract.end)}
              </div>
            )}
            <div className="small text-muted mt-1">
              {hasSheetChanges && (
                <span className="me-3">
                  Folha: {sheetByMonth.length} {pluralize(sheetByMonth.length)}
                </span>
              )}
              {summaryInfo.has_project_activity_changes && (
                <span className="me-3">
                  Atividades do projeto:{" "}
                  {summaryInfo.project_activity_months || 0}{" "}
                  {pluralize(summaryInfo.project_activity_months || 0)}
                </span>
              )}
              {summaryInfo.has_other_activity_changes && (
                <span className="me-3">
                  Outros projetos: {summaryInfo.other_activity_months || 0}{" "}
                  {pluralize(summaryInfo.other_activity_months || 0)}
                </span>
              )}
              {Math.abs(totalDeltaHours) > 0.01 && (
                <span className="me-3 d-inline-flex align-items-center gap-1">
                  {renderDeltaIcon(totalDeltaHours)}
                  {totalDeltaHours > 0 ? "+" : ""}
                  {Number(totalDeltaHours).toFixed(1)} h
                </span>
              )}
            </div>
            {badges.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {badges.map((label) => (
                  <CBadge key={label} color="secondary">
                    {label}
                  </CBadge>
                ))}
              </div>
            )}
          </div>
          <div className="d-flex flex-wrap gap-1">
            <CButton
              color="secondary"
              variant="outline"
              size="sm"
              className="d-flex align-items-center gap-1 px-2"
              onClick={() => handleTogglePerson(person.person_id)}
            >
              <CIcon icon={cilList} />
              <span>{isExpanded ? "Ocultar" : "Detalhes"}</span>
            </CButton>
            <CButton
              color="info"
              variant="outline"
              size="sm"
              className="d-flex align-items-center gap-1 px-2"
              onClick={() => {
                onRequestReview(
                  person.person_id,
                  summaryInfo.salary_only_changes && canShowSalaryFields
                    ? "salaries"
                    : undefined
                );
                onClose();
              }}
            >
              <CIcon icon={cilSpreadsheet} />
              <span>
                {summaryInfo.salary_only_changes && canShowSalaryFields
                  ? "Rever salários"
                  : "Rever"}
              </span>
            </CButton>
            {canShowSalaryFields &&
              !summaryInfo.salary_only_changes &&
              (summaryInfo.has_salary_change || summaryInfo.has_ss_change) && (
                <CButton
                  color="info"
                  variant="outline"
                  size="sm"
                  className="d-flex align-items-center gap-1 px-2"
                  onClick={() => {
                    onRequestReview(person.person_id, "salaries");
                    onClose();
                  }}
                >
                  <CIcon icon={cilSpreadsheet} />
                  <span>Rever salários</span>
                </CButton>
              )}
            <CButton
              color="success"
              variant="outline"
              size="sm"
              className="d-flex align-items-center gap-1 px-2"
              disabled={applyingPersonId === person.person_id}
              onClick={() => applyPersonChanges(person.person_id)}
            >
              {applyingPersonId === person.person_id ? (
                <CSpinner size="sm" />
              ) : (
                <CIcon icon={cilCheckCircle} />
              )}
              <span>Aplicar</span>
            </CButton>
            <CButton
              color="danger"
              variant="outline"
              size="sm"
              className="d-flex align-items-center gap-1 px-2"
              disabled={discardingPersonId === person.person_id}
              onClick={() => handleDiscardPersonChanges(person.person_id)}
            >
              {discardingPersonId === person.person_id ? (
                <CSpinner size="sm" />
              ) : (
                <CIcon icon={cilTrash} />
              )}
              <span>Descartar</span>
            </CButton>
          </div>
        </div>

        {isExpanded && (
          <div className="mt-3">
            {(hasSheetChanges ||
              hasActivityChanges ||
              hasCapacityDetails ||
              hasWorkingDayChanges) && (
              <div className="mb-3">
                <h6 className="mb-1">Alterações Pendentes Mensais</h6>
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: "12%" }}>Mês</th>
                        <th style={{ width: "23%" }}>Folha</th>
                        <th style={{ width: "25%" }}>Projeto</th>
                        <th style={{ width: "25%" }}>Outros projetos</th>
                        <th style={{ width: "15%" }}>Capacidade</th>
                        <th style={{ width: "10%" }}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyRows.map((row) => {
                        const dateKey = String(row.date);
                        const monthLabel = formatMonthLabel(row.date);
                        const projectRows = row.project_activities || [];
                        const otherRows = row.other_activities || [];
                        const alloc = row.allocation;
                        const deltaFromChanges = [
                          ...projectRows,
                          ...otherRows,
                        ].reduce((acc, r) => acc + (r.delta_real || 0), 0);
                        const monthActionKey = `${person.person_id}-${dateKey}`;
                        const monthActionState =
                          monthActionLoading[monthActionKey];
                        const monthActionBusy = Boolean(monthActionState);
                        const isApplyingMonth = monthActionState === "apply";

                        return (
                          <tr
                            key={dateKey}
                            className={
                              alloc?.over_pending ? "table-warning" : ""
                            }
                          >
                            <td>
                              <div className="fw-semibold">{monthLabel}</div>
                              <div className="small text-muted d-flex align-items-center gap-1">
                                {renderDeltaIcon(deltaFromChanges)}
                                <span>
                                  {deltaFromChanges > 0 ? "+" : ""}
                                  {deltaFromChanges.toFixed(1)} h
                                </span>
                              </div>
                            </td>
                            <td>
                              {renderSheetCell(row.sheet, row.working_days)}
                            </td>
                            <td>{renderActivitiesCell(projectRows)}</td>
                            <td>
                              {renderActivitiesCell(otherRows, {
                                highlightManaged: true,
                              })}
                            </td>
                            <td>
                              {renderCapacityCell(
                                alloc,
                                projectRows,
                                otherRows,
                                row.sheet,
                                row.working_days,
                                row.project_hours
                              )}
                            </td>
                            <td>
                              <div className="d-flex flex-wrap gap-2">
                                <CButton
                                  color="success"
                                  size="sm"
                                  variant="outline"
                                  disabled={monthActionBusy}
                                  onClick={() =>
                                    handlePersonMonthAction(
                                      person.person_id,
                                      dateKey,
                                      "apply"
                                    )
                                  }
                                >
                                  {monthActionBusy && isApplyingMonth ? (
                                    <CSpinner size="sm" />
                                  ) : (
                                    <CIcon icon={cilCheckCircle} />
                                  )}
                                </CButton>
                                <CButton
                                  color="danger"
                                  size="sm"
                                  variant="outline"
                                  disabled={monthActionBusy}
                                  onClick={() =>
                                    handlePersonMonthAction(
                                      person.person_id,
                                      dateKey,
                                      "discard"
                                    )
                                  }
                                >
                                  {monthActionBusy && !isApplyingMonth ? (
                                    <CSpinner size="sm" />
                                  ) : (
                                    <CIcon icon={cilXCircle} />
                                  )}
                                </CButton>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderPersonsSection = () => {
    if (!summary?.persons || summary.persons.length === 0) return null;

    return (
      <div className="mb-3">
        <h5 className="d-flex align-items-center gap-2">
          Colaboradores
          <CBadge color="warning">
            {summary.persons.length} com alterações
          </CBadge>
        </h5>
        <div>{summary.persons.map(renderPersonRow)}</div>
      </div>
    );
  };

  return (
    <CModal visible onClose={onClose} size="xl" backdrop="static">
      <CModalHeader>
        <CModalTitle>Alterações Pendentes</CModalTitle>
      </CModalHeader>
      <CModalBody>
        {loading && (
          <div className="d-flex justify-content-center my-4">
            <CSpinner />
          </div>
        )}

        {!loading && !hasAnyChanges && (
          <p className="text-muted my-3">
            Não existem alterações pendentes para este projeto.
          </p>
        )}

        {!loading && hasAnyChanges && (
          <>
            {isTheoretical && (
              <CAlert color="info" className="mb-3">
                Este projeto está em fase <strong>Teórica</strong>: ao aplicar
                estas alterações, o valor fica gravado simultaneamente como{" "}
                <strong>Planeado</strong> e como <strong>Real</strong> (ainda
                não há execução real distinta do planeamento).
              </CAlert>
            )}
            {projectIsSalaryBased && !salaryAccessAllowed && (
              <CAlert color="warning" className="mb-3">
                Os valores de salário e SS foram ocultados para este cliente.
                Pode continuar a analisar as restantes alterações.
              </CAlert>
            )}
            {renderOverview()}
            {renderWorkingDaysSection()}
            {renderPersonsSection()}
          </>
        )}
      </CModalBody>
      <CModalFooter className="d-flex justify-content-between">
        <div className="text-muted small">
          Pode aplicar todas as alterações ou apenas as alterações de um
          colaborador de cada vez.
        </div>
        <div className="d-flex flex-wrap gap-2 justify-content-end">
          <CButton color="secondary" variant="outline" onClick={onClose}>
            Fechar
          </CButton>
          <CButton
            color="danger"
            variant="outline"
            disabled={discardingAll || !hasAnyChanges}
            onClick={handleDiscardAllChanges}
          >
            {discardingAll ? (
              <CSpinner size="sm" className="me-1" />
            ) : (
              <CIcon icon={cilTrash} className="me-1" />
            )}
            Descartar alterações
          </CButton>
          <CButton
            color="success"
            disabled={applyingAll || !hasAnyChanges}
            onClick={applyAllChanges}
          >
            {applyingAll && <CSpinner size="sm" className="me-1" />}
            Aplicar todas as alterações
          </CButton>
        </div>
      </CModalFooter>
    </CModal>
  );
}
